import React from 'react';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {DndProvider} from 'react-dnd';
import {createHashRouter, RouterProvider} from 'react-router-dom';
import {ToastContainer} from 'components/ToastContainer';
import {store} from '../index';
import {AppBridgeEventPageMount} from '../model/appBridge';
import {AppProviders} from './AppProviders';
import {ContentViewerPage} from 'pages/ContentViewer/ContentViewerPage';
import {CodebooksPage} from 'pages/CodebooksPage';
import {NotFoundPage} from 'pages/ContentViewer/404';
import {BillingPage} from 'pages/BillingPage';
import {CompanySettingsPage} from 'pages/CompanySettingsPage';
import {fetchAuthUser} from '../store/auth';
import ErrorBoundary from 'components/ErrorBoundary';
import {CataloguesPage} from "pages/CataloguesPage";
import {PublicOfferPage} from "pages/PublicOfferPage";
import {PrivateOfferPage} from "pages/PrivateOfferPage";
import {UserGroupsPage} from "pages/UserGroupsPage";
import {ReportsPage} from "pages/ReportsPage";
import {ApprovalsPage} from "pages/ApprovalsPage";
import {ApprovalDetailPage} from "pages/ApprovalDetailPage";
import {ApprovalFormPage} from "pages/ApprovalFormPage";
import {StudentApprovalsPage} from "pages/StudentApprovalsPage";
import {StudentRequestsPage} from "pages/StudentRequestsPage";
import {StudentRequestFormPage} from "pages/StudentRequestFormPage";
import {EditorPage} from "pages/EditorTest/EditorPage";
import {KlaraPage} from "pages/Klara/KlaraPage";
import {GuestRestricted} from "components/GuestRestricted";

function App({appEvent}: { appEvent: AppBridgeEventPageMount }) {
    // useEffect(() => {
    //   console.log('React page mounted', appEvent.page);
    //   return () => console.log('React page UNmounted', appEvent.page);
    // }, [appEvent]);

    // fetch active user on react screens
    store.dispatch(fetchAuthUser());

    const router = createHashRouter([
        {
            path: 'learn/:courseId',
            element: <ContentViewerPage/>,
            children: [
                {
                    path: 'certificate',
                    element: <ContentViewerPage/>,
                },
                {
                    path: 'lesson/:lessonId',
                    element: <ContentViewerPage/>,
                    children: [
                        {
                            path: 'step/:stepId',
                            element: <ContentViewerPage/>,
                        },
                    ],
                },
            ],
        },
        {
            path: 'editor/:courseId/step/:stepId',
            element: <EditorPage type={appEvent.data?.type}/>,
        },
        {
            path: 'csPreferences/codebooks',
            // the tree switch travels on the bridge; an older shell that does not send it
            // leaves the editor as it always was
            element: (
                <GuestRestricted>
                    <CodebooksPage
                        isTreeEnabled={appEvent.data?.workplaceTreeEnabled !== false}
                        isMultipleWorkplacesEnabled={!!appEvent.data?.multipleWorkplacesEnabled}
                    />
                </GuestRestricted>
            ),
        },
        {
            path: 'csPreferences/general',
            element: <GuestRestricted><CompanySettingsPage/></GuestRestricted>,
        },
        {
            path: 'csPreferences/billing',
            element: <GuestRestricted><BillingPage/></GuestRestricted>,
        },
        {
            path: 'catalogues',
            element: <GuestRestricted><CataloguesPage/></GuestRestricted>,
            children: [
                {
                    path: ':catalogueId',
                    element: <GuestRestricted><CataloguesPage/></GuestRestricted>,
                    children: [
                        {
                            path: ':pageType',
                            element: <GuestRestricted><CataloguesPage/></GuestRestricted>,
                            children: [
                                {
                                    path: ':pageId',
                                    element: <GuestRestricted><CataloguesPage/></GuestRestricted>,
                                },
                            ]
                        }
                    ]
                },
            ]
        },
        {
            path: 'contentManager',
            element: <GuestRestricted>{appEvent.data?.type === 'private' ? <PrivateOfferPage/> : <PublicOfferPage/>}</GuestRestricted>,
            children: [
                {
                    path: ':contentId',
                    element: <GuestRestricted>{appEvent.data?.type === 'private' ? <PrivateOfferPage/> : <PublicOfferPage/>}</GuestRestricted>,
                    children: [
                        {
                            path: 'create-offer',
                            element: <GuestRestricted>{appEvent.data?.type === 'private' ? <PrivateOfferPage/> : <PublicOfferPage/>}</GuestRestricted>,
                        }
                    ]
                },
            ]
        },
        {
            path: 'planManagement',
            element: <GuestRestricted><UserGroupsPage tab={appEvent.data?.tab}/></GuestRestricted>,
            children: [
                {
                    path: 'plans',
                    element: <GuestRestricted><UserGroupsPage tab={appEvent.data?.tab}/></GuestRestricted>,
                    children: [
                        {
                            path: ':sub',
                            element: <GuestRestricted><UserGroupsPage tab={appEvent.data?.tab}/></GuestRestricted>,
                        }
                    ]
                },
            ]
        },
        {
            path: 'approvals',
            element: <ApprovalsPage/>,
        },
        {
            path: 'dashboard/approvals',
            element: <StudentApprovalsPage/>,
        },
        {
            path: 'dashboard/requests',
            element: <StudentRequestsPage/>,
        },
        {
            path: 'dashboard/requests/new',
            element: <StudentRequestFormPage/>,
        },
        // the student's own records keep their detail under Studium: the left
        // menu highlights the active top-level Angular state, and /approvals
        // would light up the manager's queue instead
        {
            path: 'dashboard/requests/:approvalId',
            element: <ApprovalDetailPage/>,
        },
        {
            path: 'dashboard/approvals/:approvalId',
            element: <ApprovalDetailPage/>,
        },
        {
            path: 'approvals/new',
            element: <ApprovalFormPage/>,
        },
        {
            path: 'approvals/:approvalId',
            element: <ApprovalDetailPage/>,
        },
        {
            path: 'approvals/:approvalId/edit',
            element: <ApprovalFormPage/>,
        },
        {
            path: 'adminDashboard',
            element: <GuestRestricted><ReportsPage/></GuestRestricted>,
            children: [
                {
                    path: ':ignored',
                    element: <div></div>,
                },
            ]
        },
        {
            // One route with a splat rather than nested levels each rendering
            // this page: KlaraPage renders no Outlet, so only the outermost
            // match ever mounted and its params never included the deeper
            // segments. Mirrors the Angular states, so both routers read the
            // same hash.
            path: 'klara/*',
            element: <KlaraPage/>,
        },
        {
            path: 'notFound',
            element: <NotFoundPage/>,
        },
    ]);

    return (
        <ErrorBoundary>
            <AppProviders store={store}>
                <DndProvider backend={HTML5Backend}>
                    <ToastContainer/>
                    <RouterProvider router={router}/>
                </DndProvider>
            </AppProviders>
        </ErrorBoundary>
    );
}

export default App;
